/**
 * Admin → Link Health.
 *
 * Shows everyone still stranded on the old `linked_players` table and, per
 * person, the one thing they're waiting on. Two actions:
 *   • Migrate — we already know their character id, so their rows can be
 *     written right now.
 *   • Nudge   — their legacy row only holds a console/steam id, so nothing
 *     can be written until they log in. DM asks them to hop in game once.
 */
import { useCallback, useEffect, useState } from "react";
import { HeartPulse, Send, ArrowRightLeft, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader } from "@/components/Atoms";

const ID_KIND_LABEL = {
  playeruid: "character id",
  steam: "Steam id",
  xbox: "Xbox id",
  playstation: "PlayStation id",
  unknown: "unrecognised id",
  empty: "no id",
};

function when(value) {
  if (!value) return "never";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function LinkHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(() => new Set());

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/link-health")
      .then(({ data: d }) => { setData(d); setPicked(new Set()); setError(""); })
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const toggle = (id) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const run = async (action) => {
    const ids = [...picked].filter((id) => {
      const person = data.people.find((p) => p.discord_id === id);
      return person && person.action === action;
    });
    if (!ids.length) {
      toast.error(action === "nudge"
        ? "Pick someone who's waiting on a login first"
        : "Pick someone whose character id we already know");
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/link-health/${action}`, { discord_ids: ids });
      if (action === "nudge") {
        toast.success(`${res.sent} DM${res.sent === 1 ? "" : "s"} sent`
          + (res.skipped ? `, ${res.skipped} skipped` : "")
          + (res.failed ? `, ${res.failed} failed` : ""));
      } else {
        toast.success(`${res.migrated} migrated`
          + (res.skipped ? `, ${res.skipped} skipped` : ""));
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;
  if (error) {
    return (
      <div className="glass-card p-5 text-sm text-[var(--error)]" data-testid="link-health-error">
        {error}
      </div>
    );
  }

  const people = data?.people || [];

  return (
    <div className="space-y-4 fade-up" data-testid="link-health-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <HeartPulse size={18} className="text-[var(--teal)]" />
            Link Health
          </h2>
          <p className="text-sm text-[var(--muted)] mt-1 max-w-2xl">
            These players are linked on the old system only, so the shop, rewards and
            giveaway delivery can't see them.
          </p>
        </div>
        <button
          onClick={load}
          className="btn btn-sm btn-secondary flex items-center gap-1.5"
          data-testid="link-health-refresh"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          ["Still on old system", data.total, "link-health-total"],
          ["Ready to migrate", data.ready_to_migrate, "link-health-ready"],
          ["Waiting on a login", data.awaiting_login, "link-health-awaiting"],
        ].map(([label, value, tid]) => (
          <div key={tid} className="glass-card p-4" data-testid={tid}>
            <p className="text-xs uppercase tracking-wider text-[var(--dim)]">{label}</p>
            <p className="text-2xl font-semibold glow-teal mt-1">{value}</p>
          </div>
        ))}
      </div>

      {!data.dm_enabled && (
        <div
          className="glass-card p-3 flex items-center gap-2 text-xs text-[var(--warn)]"
          data-testid="link-health-dm-disabled"
        >
          <ShieldAlert size={14} />
          BOT_TOKEN isn't configured, so DMs can't be sent from here.
        </div>
      )}

      {people.length === 0 ? (
        <div className="glass-card p-6 text-sm text-[var(--muted)]" data-testid="link-health-empty">
          Nobody is stuck — every linked player is on the current system.
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="flex items-center justify-between gap-2 p-3 border-b border-white/5 flex-wrap">
            <span className="text-xs text-[var(--muted)]" data-testid="link-health-selection">
              {picked.size} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => run("migrate")}
                disabled={busy}
                className="btn btn-sm btn-secondary flex items-center gap-1.5"
                data-testid="link-health-migrate-btn"
              >
                <ArrowRightLeft size={13} /> Migrate selected
              </button>
              <button
                onClick={() => run("nudge")}
                disabled={busy || !data.dm_enabled}
                className="btn btn-sm flex items-center gap-1.5"
                data-testid="link-health-nudge-btn"
              >
                <Send size={13} /> Send nudge
              </button>
            </div>
          </div>

          <ul className="divide-y divide-white/5">
            {people.map((p) => (
              <li
                key={p.discord_id}
                className="p-3 flex items-start gap-3 hover:bg-white/[0.02] transition-colors"
                data-testid={`link-health-row-${p.discord_id}`}
              >
                <input
                  type="checkbox"
                  checked={picked.has(p.discord_id)}
                  onChange={() => toggle(p.discord_id)}
                  className="mt-1 accent-[var(--teal)]"
                  data-testid={`link-health-check-${p.discord_id}`}
                  aria-label={`Select ${p.player_name}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{p.player_name || "(no name)"}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${
                        p.action === "migrate"
                          ? "border-[var(--teal-soft)] text-[var(--teal)]"
                          : "border-amber-400/40 text-amber-300"
                      }`}
                      data-testid={`link-health-status-${p.discord_id}`}
                    >
                      {p.action === "migrate" ? "ready" : "needs login"}
                    </span>
                    {p.discord_username && (
                      <span className="text-xs text-[var(--muted)]">@{p.discord_username}</span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--muted)] mt-1">{p.waiting_on}</p>
                  <p className="text-[11px] text-[var(--dim)] mt-1 font-mono break-all">
                    {ID_KIND_LABEL[p.stored_id_kind] || p.stored_id_kind}: {p.stored_id || "—"}
                    {" · "}linked {when(p.linked_at)}
                    {p.action === "nudge" && (
                      <>
                        {" · "}nudged {when(p.last_nudged_at)}
                        {p.nudge_count > 1 ? ` (${p.nudge_count}×)` : ""}
                      </>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
